import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * LiveTournamentStrip — hero-style banner shown when a tournament is LIVE.
 * Visual language: transparent glass + deep red radial glow, beveled cut
 * edge on the right, animated red pulse dot. No boxy rectangle.
 */
export default function LiveTournamentStrip({ tournament, extraLiveCount = 0 }) {
  if (!tournament) return null;

  const subtitle = (() => {
    if (tournament.game) return tournament.game;
    if (tournament.description) return tournament.description;
    return "Competitive tournament";
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="live-tournament-strip"
      className="relative"
    >
      <Link
        to={`/tournament/${tournament.id}/bracket`}
        data-testid="live-tournament-strip-link"
        className="group relative block overflow-hidden
                   rounded-[28px] md:rounded-[999px] px-5 md:px-8 py-3.5
                   border border-red-500/35
                   transition-all duration-300
                   hover:border-red-400/60 hover:shadow-[0_0_45px_rgba(230,0,0,0.35)]"
        style={{
          background:
            "linear-gradient(95deg, rgba(230,0,0,0.25) 0%, rgba(230,0,0,0.08) 35%, rgba(0,0,0,0.55) 70%, rgba(230,0,0,0.12) 100%)",
          backdropFilter: "blur(14px) saturate(140%)",
          WebkitBackdropFilter: "blur(14px) saturate(140%)",
        }}
      >
        {/* animated red glow pulse behind the whole strip */}
        <div className="pointer-events-none absolute inset-0 -z-10 live-glow-breathe opacity-60" />
        {/* top sheen */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)" }}
        />
        {/* scan-line sweep on hover */}
        <div
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,60,60,0.08) 50%, transparent 100%)",
            mixBlendMode: "screen",
          }}
        />

        <div className="relative flex items-center gap-3 md:gap-4 min-h-[44px]">
          {/* LIVE badge */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 shadow-[0_0_14px_rgba(255,40,40,0.95)]" />
            </span>
            <span className="font-mono font-bold text-[11px] tracking-[0.35em] text-red-300 leading-none">
              LIVE
            </span>
          </div>

          {/* Tournament name + game on a shared text baseline */}
          <div className="flex items-baseline gap-3 md:gap-4 min-w-0">
            <span
              data-testid="live-tournament-strip-name"
              className="font-display font-bold text-white text-[15px] sm:text-base md:text-lg truncate"
            >
              {tournament.name}
            </span>
            {subtitle && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/90 truncate shrink-0">
                {subtitle}
              </span>
            )}
          </div>

          {extraLiveCount > 0 && (
            <span className="hidden md:inline-flex font-mono text-[10px] tracking-widest text-red-300/90 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5 shrink-0 leading-none">
              +{extraLiveCount} MORE LIVE
            </span>
          )}

          {/* spacer pushes CTA to far right */}
          <span className="flex-1" />

          {/* CTA */}
          <div
            className="hidden sm:flex items-center gap-1.5 shrink-0
                       font-mono text-[10px] tracking-[0.3em] uppercase
                       text-red-200 group-hover:text-white transition-colors leading-none"
          >
            View Bracket
            <ArrowRight
              size={12}
              className="transition-transform duration-300 group-hover:translate-x-1.5"
            />
          </div>
          <ArrowRight
            size={14}
            className="sm:hidden text-red-300 shrink-0 group-hover:translate-x-1 transition-transform"
          />
        </div>
      </Link>
    </motion.div>
  );
}
